import { useCallback, useEffect, useState } from 'react';
import {
	getUserProperty,
	setUserProperties,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';

export const useShowProjectPagesSpotlightSeenStatus = (
	messageId: string,
): [boolean, () => void] => {
	const [showSpotlight, setShowSpotlight] = useState(false);
	const propertyKey = `${messageId}.seen`;
	const accountId = useAccountId();

	useEffect(() => {
		if (accountId) {
			getUserProperty(accountId, propertyKey)
				.then(() => {
					setShowSpotlight(false);
				})
				.catch((error) => {
					// If we can't find key, we know user never seen the spotlight
					if (error.statusCode === 404) {
						setShowSpotlight(true);
						return;
					}

					log.safeErrorWithoutCustomerData(
						'useShowProjectPagesSpotlightSeenStatus',
						`Error reading data from getUserProperty with property key ${propertyKey}`,
						error,
					);
				});
		} else {
			log.safeErrorWithoutCustomerData(
				'useShowProjectPagesSpotlightSeenStatus',
				`Can not read data from getUserProperty with property key ${propertyKey} because accountId is missing`,
			);
		}
	}, [accountId, propertyKey]);

	const markAsSeen = useCallback(() => {
		if (accountId) {
			setUserProperties(accountId, propertyKey, 'true')
				.then((res) => {
					if (res.ok) {
						return;
					}

					res.text().then((errResp: string) => {
						throw new FetchError(res.status, errResp);
					});
				})
				.catch((error) =>
					log.safeErrorWithoutCustomerData(
						'useShowProjectPagesSpotlightSeenStatus',
						`Failed to update the value in setUserProperties with property key ${propertyKey}`,
						error,
					),
				);
		} else {
			log.safeErrorWithoutCustomerData(
				'useShowProjectPagesSpotlightSeenStatus',
				`Can not set data using setUserProperties with property key ${propertyKey} because accountId is missing`,
			);
		}
	}, [accountId, propertyKey]);

	return [showSpotlight, markAsSeen];
};
